import { createApi } from '@reduxjs/toolkit/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import { REDUX_CACHE, EXERCISE_CONFIG, HTTP_METHOD } from '../constants';
import * as endpoints from '../endpoints';
import { IExerciseOption } from '../../../types/interfaces/exercise-option';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { IAppFile } from '../../../types/interfaces/app-file';

export const exercisesConfigApi = createApi({
  reducerPath: 'exerciseConfigApi',
  tagTypes: [
    EXERCISE_CONFIG.EXERCISE_CATEGORIES,
    EXERCISE_CONFIG.EXERCISE_OPTIONS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getExerciseOptionsApi: builder.query<IExerciseOption[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.ExerciseOption.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_CONFIG.EXERCISE_OPTIONS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getExercisePropertiesApi: builder.query<IExerciseProperty[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.ExerciseProperties.ROOT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, EXERCISE_CONFIG.EXERCISE_CATEGORIES),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutExerciseOption: builder.mutation<
      void,
      { id: string; body: IExerciseOption }
    >({
      query({ id, body }) {
        return {
          url: endpoints.ExerciseOption.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_CONFIG.EXERCISE_OPTIONS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_CONFIG.EXERCISE_OPTIONS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise option'),
    }),
    deleteExerciseOption: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.ExerciseOption.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_CONFIG.EXERCISE_OPTIONS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise option'),
    }),
    postPutExerciseProperty: builder.mutation<
      void,
      { id: string; body: IExerciseProperty & { avatar?: IAppFile } }
    >({
      query({ id, body }) {
        return {
          url: endpoints.ExerciseProperties.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_CONFIG.EXERCISE_CATEGORIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: EXERCISE_CONFIG.EXERCISE_CATEGORIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise category'),
    }),
    deleteExerciseProperty: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.ExerciseProperties.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: EXERCISE_CONFIG.EXERCISE_CATEGORIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Exercise category'),
    }),
  }),
});

export const {
  useGetExerciseOptionsApiQuery: useGetExerciseOptions,
  usePostPutExerciseOptionMutation: usePostPutExerciseOption,
  useDeleteExerciseOptionMutation: useDeleteExerciseOption,
  useGetExercisePropertiesApiQuery: useGetExerciseProperties,
  usePostPutExercisePropertyMutation: usePostPutExerciseProperty,
  useDeleteExercisePropertyMutation: useDeleteExerciseProperty,
} = exercisesConfigApi;

export const resetExerciseConfigApiState = () =>
  exercisesConfigApi.util.resetApiState();
